import React from "react";
import styled from "styled-components";
import Image from "next/image";

import * as T from "./Typography";
import * as I from "../components/Icons";
import * as U from "../components/Utils";
import * as u from "../utils";
import * as formatDateAndTime from "../utils/formatDateAndTime";
import { FaClock, FaMapMarkerAlt } from "react-icons/fa";
import RxLink from "./ui/inputs/RxLink";

export default function EventCard(props) {
  const isClub = props.memberAccess === "CLUB";
  const isFull = props.ticketCapacity <= getAttendeesNumber();

  return (
    <S.EventContainer
      data-cy={`event-card--${props.id}`}
      href={`/events/${props.id}`}
    >
      <S.ImageWrapper>
        {props.cardImage && (
          <S.StyledImage
            className="image"
            loader={() => props.cardImage}
            src={props.cardImage}
            alt={"Attend " + props.title}
            layout="fill"
            objectFit="cover"
            data-cy="eventCardImage"
          />
        )}
      </S.ImageWrapper>
      {isFull && (
        <S.Badge background={u.colours.freeBanana}>
          <T.CS>SOLD OUT</T.CS>
        </S.Badge>
      )}
      {isClub && !isFull && (
        <S.Badge background={u.colours.teal}>
          <T.CS colour={"white"}>AllBright@ Old Sessions House</T.CS>
        </S.Badge>
      )}
      {/* {IS LIVE CLASS && (
            <S.Badge background={u.colours.kimberlyPurple}>
              <T.CS colour={"white"}>Live Class</T.CS>
            </S.Badge>
          )} */}
      <S.Wrapper className="content-box">
        <S.FlexA>
          <T.CS
            colour={u.colours.textTurquoise}
            weight={"bold"}
            data-cy="startTime"
          >
            {formatDateAndTime.format(props.startTime)}
          </T.CS>
          <U.Space height={5} />
          <S.Desktop>
            <T.H5 weight={"bold"}>{props.title}</T.H5>
          </S.Desktop>
          <S.Mobile>
            <T.H5 weight={"bold"}>{props.title}</T.H5>
          </S.Mobile>
          <S.LocationDurationContainer>
            <S.DurationWrapper>
              <S.Clock color={u.colours.textTurquoise} />
              {props.duration > 0 && (
                <T.CS weight="700">
                  {formatDateAndTime.convertMinutesToHours(props.duration)}
                </T.CS>
              )}
            </S.DurationWrapper>
            <S.LocationWrapper>
              <S.MapIcon color={u.colours.textTurquoise} />
              <T.CS data-cy="location" weight={500}>
                {(isClub && !props.location && "Old Sessions House, London") ||
                  props.location ||
                  "Online"}
              </T.CS>
            </S.LocationWrapper>
          </S.LocationDurationContainer>
        </S.FlexA>
        {props.showLock && !props.isFree && <S.Lock />}
      </S.Wrapper>
    </S.EventContainer>
  );

  function getAttendeesNumber() {
    const toTotal = (a, e) => a + 1 + (+e.guests || 0);
    return props?.attendees?.reduce(toTotal, 0);
  }
}

const S = {};

S.EventContainer = styled(RxLink)`
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);

  &::after {
    border-radius: 3px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    content: "";
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
    width: 100%;
    z-index: -1;
  }

  &:hover .content-box {
    transform: scale(1.05);
    filter: brightness(1.05);
  }

  &:hover .image {
    transform: scale(1.05);
  }

  &:hover .link-text {
    opacity: 1;
  }

  &:hover .link-text::after {
    width: 100%;
  }
`;

S.Wrapper = styled.div`
  border-radius: 5px;
  z-index: 1;
  width: 90%;
  align-self: center;
  box-shadow: 0 0 15px ${u.colours.charcoalFaded};
  margin: -20px 0 0 0;
  border-bottom: solid 5px ${u.colours.textTurquoise};
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  padding: 10px;
  background: linear-gradient(45deg, ${u.colours.shell}, ${u.colours.white});
  transition: all 0.3s ease-out;
  outline: solid 0px ${u.colours.textTurquoise};

  @media ${u.device.tablet} {
    padding: 15px 10px 20px;
  }

  @media ${u.device.desktop} {
    padding: 15px 20px 20px;
  }
`;

S.FlexA = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

S.FlexB = styled.div`
  display: flex;
  justify-content: space-between;
`;

S.LocationWrapper = styled.div`
  display: flex;
  justify-self: flex-start;
`;

S.DurationWrapper = styled.div`
  display: flex;
  margin-top: 10px;
`;

S.LocationDurationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  height: 100%;
`;

S.Clock = styled(FaClock)`
  margin-left: 2px;
  margin-right: 5px;
`;

S.MapIcon = styled(FaMapMarkerAlt)`
  color: ${u.colours.teal};
  margin-right: 3px;
  min-width: 20px;
  min-height: 20px;
`;

S.Lock = styled(I.Lock)`
  color: ${u.colours.charcoal};
  height: 18px;
  width: 14px;
`;

S.ImageWrapper = styled.div`
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  width: 100%;
  aspect-ratio: 1;

  &:hover {
  }

  @media ${u.device.desktop} {
    width: 100%;
    aspect-ratio: 1;
  }
`;

S.StyledImage = styled(Image)`
  object-fit: cover;
  transition: all 0.3s ease-out;
`;

S.TopRight = styled.div`
  position: absolute;
  padding: 0px 5px;
  height: 20px;
  background: rgb(250, 125, 0);
  top: 0;
  right: 0;
  border-radius: 0 0 0 4px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

S.TopRightClub = styled.div`
  position: absolute;
  padding: 0px 5px;
  height: 20px;
  background: ${u.colours.textTurquoise};
  top: 0;
  right: 0;
  border-radius: 0 0 0 4px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

S.Badge = styled.div`
  position: absolute;
  padding: 0px 5px;
  height: 20px;
  background: ${(p) => p.background};
  top: 0;
  right: 0;
  border-radius: 0 0 0 4px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

S.Desktop = styled.div`
  display: none;

  @media ${u.device.tablet} {
    display: flex;
  }
`;
S.Mobile = styled.div`
  display: flex;

  @media ${u.device.tablet} {
    display: none;
  }
`;
